export type RateMode = "ocean" | "air" | "road";
export type RateService = "fcl" | "lcl" | "ftl" | "ltl" | "air";
export type RateStatus = "ready" | "review" | "rejected";

export type RateIssueCode = "validity" | "charges" | "equip" | "routing" | "dup" | "expired";

export type RateRecord = {
  id: string;
  status: RateStatus;
  mode: RateMode;
  service: RateService;
  carrier: string;
  /** SCAC when known; TBN when Carrier TBN. */
  carrierCode?: string;
  origin: string;
  destination: string;
  terms: string;
  termsNote: string;
  equipment: string;
  transit: string;
  currency: string;
  amount: number | null;
  unit: string;
  validity: string;
  /** Comma-separated transshipment ports (edit-friendly). */
  transshipment: string;
  includesOriginThc: boolean | null;
  includesDestinationThc: boolean | null;
  issues: string[];
  issueCodes: RateIssueCode[];
};

/** Concrete container types for RateCard edit + guided equipment chips. */
export const EQUIPMENT = ["20'ST", "20'DV", "40'ST", "40'HC", "40'HQ", "40'RF", "45'HC"] as const;

/** Edit UI options for API `unit`. */
export const UNIT_OPTIONS = [
  { value: "container", label: "per container" },
  { value: "kg", label: "per kg" },
  { value: "truck", label: "per truck" },
  { value: "wm", label: "per W/M" },
  { value: "shipment", label: "per shipment" },
] as const;

export const SERVICE_OPTIONS: { value: RateService; label: string }[] = [
  { value: "fcl", label: "FCL" },
  { value: "lcl", label: "LCL" },
  { value: "ftl", label: "FTL" },
  { value: "ltl", label: "LTL" },
  { value: "air", label: "Air" },
];

export const MODE_OPTIONS: { value: RateMode; label: string }[] = [
  { value: "ocean", label: "Ocean" },
  { value: "air", label: "Air" },
  { value: "road", label: "Road" },
];

/** Liner terms (FIFO/FILO/LIFO/LILO) derived from includes_*_thc flags. */
export type LinerTermChoice = "fifo" | "filo" | "lifo" | "lilo";

export const LINER_TERM_OPTIONS: { value: LinerTermChoice; label: string }[] = [
  { value: "fifo", label: "FIFO" },
  { value: "filo", label: "FILO" },
  { value: "lifo", label: "LIFO" },
  { value: "lilo", label: "LILO" },
];

export const linerTermLabels = (choice: LinerTermChoice): { title: string; subtitle: string } => {
  if (choice === "fifo") {
    return { title: "Port to Port (FIFO)", subtitle: "Loading & unloading excluded" };
  }
  if (choice === "filo") {
    return {
      title: "Port to Port (FILO)",
      subtitle: "Origin loading excluded · destination unloading included",
    };
  }
  if (choice === "lifo") {
    return {
      title: "Port to Port (LIFO)",
      subtitle: "Origin loading included · destination unloading excluded",
    };
  }
  // LILO (and any unexpected value → FIFO display; Unspecified is removed)
  return { title: "Port to Port (LILO)", subtitle: "Loading & unloading included" };
};

/**
 * Map includes_*_thc → liner term.
 * Exact both-end flags win; legacy dest-only (origin null) maps to FILO/FIFO
 * to match Charges clarification Included → FILO / Excluded → FIFO.
 */
export const linerTermFromRate = (
  rate: Pick<RateRecord, "includesOriginThc" | "includesDestinationThc">
): LinerTermChoice => {
  const origin = rate.includesOriginThc;
  const dest = rate.includesDestinationThc;

  if (origin === false && dest === false) return "fifo";
  if (origin === false && dest === true) return "filo";
  if (origin === true && dest === false) return "lifo";
  if (origin === true && dest === true) return "lilo";
  // Legacy / clarification dest-only cues
  if (origin == null && dest === true) return "filo";
  if (origin == null && dest === false) return "fifo";

  // Both null (or mixed unknown) → default FIFO (Unspecified removed)
  return "fifo";
};

export const thcFieldsFromLinerTerm = (
  choice: LinerTermChoice
): Pick<RateRecord, "includesOriginThc" | "includesDestinationThc"> => {
  if (choice === "filo") return { includesOriginThc: false, includesDestinationThc: true };
  if (choice === "lifo") return { includesOriginThc: true, includesDestinationThc: false };
  if (choice === "lilo") return { includesOriginThc: true, includesDestinationThc: true };

  // fifo (default)
  return { includesOriginThc: false, includesDestinationThc: false };
};

/** Apply liner term flags and derived title/subtitle onto a rate draft. */
export const applyLinerTerm = (rate: RateRecord, choice: LinerTermChoice): RateRecord => {
  const labels = linerTermLabels(choice);

  return {
    ...rate,
    ...thcFieldsFromLinerTerm(choice),
    terms: labels.title,
    termsNote: labels.subtitle,
  };
};

/** True when equipment is a concrete container type (not empty / dash / not stated). */
export const isConcreteEquipment = (value: string): boolean => {
  const text = value.trim();

  return text !== "" && text !== "—" && text !== "-" && !/^not stated$/i.test(text);
};

const portFilled = (value: string): boolean => {
  const text = value.trim();

  return text !== "" && text !== "—" && text !== "-" && !/^not stated$/i.test(text) && !/^port unclear/i.test(text);
};

export type RateBucket = RateStatus | "all";

export type RatesStep = "overview" | "import" | "library" | "pricing" | "published";

export type PricingRule = {
  id: string;
  title: string;
  origin: string;
  destination: string;
  equipment: string;
  action: string;
};

export const RATE_STEPS: { key: RatesStep; step: number; label: string }[] = [
  { key: "import", step: 1, label: "Import & Parse" },
  { key: "library", step: 2, label: "Data Review & Edit" },
  { key: "pricing", step: 3, label: "Pricing Rules" },
  { key: "published", step: 4, label: "Approve & Publish" },
];

export const BUCKET_LABEL: Record<RateBucket, string> = {
  all: "All rates",
  ready: "Clean Rates",
  review: "Needs Information",
  rejected: "Rejected",
};

export const EQUIPMENT_OPTIONS = [
  { value: "", label: "Any equipment" },
  { value: "20", label: "20' Standard (FCL)" },
  { value: "40", label: "40' Standard (FCL)" },
  { value: "HC", label: "40' High Cube (FCL)" },
  { value: "kg", label: "Air freight (per kg)" },
];

const FIFO = "Port to Port (FIFO)";
const EXCLUDED = "Loading & unloading excluded";

export const SEED_RATES: RateRecord[] = [
  {
    id: "64179323",
    status: "ready",
    mode: "ocean",
    service: "fcl",
    carrier: "Maersk",
    origin: "Ningbo-Zhoushan Port, CN",
    destination: "Khor al Fakkan, AE",
    terms: FIFO,
    termsNote: EXCLUDED,
    equipment: "20'ST",
    transit: "31 days",
    currency: "USD",
    amount: 5050,
    unit: "container",
    validity: "01 Aug – 30 Sep 2026",
    transshipment: "",
    includesOriginThc: false,
    includesDestinationThc: false,
    issues: [],
    issueCodes: [],
  },
  {
    id: "64179341",
    status: "ready",
    mode: "ocean",
    service: "fcl",
    carrier: "Hapag-Lloyd",
    origin: "Shanghai, CN",
    destination: "Rotterdam, NL",
    terms: FIFO,
    termsNote: EXCLUDED,
    equipment: "40'HC",
    transit: "32 days",
    currency: "USD",
    amount: 2140,
    unit: "container",
    validity: "15 Jul – 15 Oct 2026",
    transshipment: "",
    includesOriginThc: false,
    includesDestinationThc: false,
    issues: [],
    issueCodes: [],
  },
  {
    id: "64179356",
    status: "ready",
    mode: "ocean",
    service: "fcl",
    carrier: "ONE",
    origin: "Jebel Ali, AE",
    destination: "Mundra, IN",
    terms: FIFO,
    termsNote: EXCLUDED,
    equipment: "20'DV",
    transit: "6 days",
    currency: "USD",
    amount: 540,
    unit: "container",
    validity: "01 Sep – 31 Dec 2026",
    transshipment: "",
    includesOriginThc: false,
    includesDestinationThc: false,
    issues: [],
    issueCodes: [],
  },
  {
    id: "64179401",
    status: "review",
    mode: "ocean",
    service: "fcl",
    carrier: "CMA CGM",
    origin: "Shanghai, CN",
    destination: "Jebel Ali, AE",
    terms: FIFO,
    termsNote: EXCLUDED,
    equipment: "40'HQ",
    transit: "21 days",
    currency: "USD",
    amount: 1450,
    unit: "container",
    validity: "Not stated",
    transshipment: "",
    includesOriginThc: false,
    includesDestinationThc: false,
    issues: ["Validity not stated"],
    issueCodes: ["validity"],
  },
  {
    id: "64179412",
    status: "review",
    mode: "ocean",
    service: "fcl",
    carrier: "MSC",
    origin: "Antwerp, BE",
    destination: "Santos, BR",
    terms: FIFO,
    termsNote: EXCLUDED,
    equipment: "20'DV",
    transit: "19 days",
    currency: "USD",
    amount: 980,
    unit: "container",
    validity: "Not stated",
    transshipment: "",
    includesOriginThc: false,
    includesDestinationThc: false,
    issues: ["Destination ambiguous: two values found", "Validity end date missing"],
    issueCodes: ["validity", "routing"],
  },
  {
    id: "64179425",
    status: "review",
    mode: "ocean",
    service: "fcl",
    carrier: "Carrier TBN",
    carrierCode: "TBN",
    origin: "Shanghai, CN",
    destination: "Jebel Ali, AE",
    terms: "Port to Port (FIFO)",
    termsNote: "Loading & unloading excluded",
    equipment: "40'HQ",
    transit: "—",
    currency: "USD",
    amount: 1450,
    unit: "container",
    validity: "Not stated",
    transshipment: "",
    includesOriginThc: false,
    includesDestinationThc: false,
    issues: ["Validity not stated"],
    issueCodes: ["validity"],
  },
  {
    id: "64179431",
    status: "review",
    mode: "ocean",
    service: "fcl",
    carrier: "Evergreen",
    origin: "Port unclear (“NB”)",
    destination: "Hamburg, DE",
    terms: "Pricing structure unclear",
    termsNote: "Per-container vs per-shipment not stated",
    equipment: "not stated",
    transit: "30 days",
    currency: "USD",
    amount: 1960,
    unit: "container",
    validity: "Not stated",
    transshipment: "",
    includesOriginThc: null,
    includesDestinationThc: null,
    issues: ["Origin port not recognised", "Container type not stated"],
    issueCodes: ["equip", "routing"],
  },
  {
    id: "64179440",
    status: "rejected",
    mode: "ocean",
    service: "fcl",
    carrier: "Source row 118",
    origin: "Corrupted cell",
    destination: "Corrupted cell",
    terms: "Row could not be reconstructed",
    termsNote: "Merged cells with mixed content",
    equipment: "—",
    transit: "—",
    currency: "USD",
    amount: null,
    unit: "—",
    validity: "Not stated",
    transshipment: "",
    includesOriginThc: null,
    includesDestinationThc: null,
    issues: ["Origin and destination empty", "Rate empty"],
    issueCodes: [],
  },
  {
    id: "64179447",
    status: "rejected",
    mode: "ocean",
    service: "fcl",
    carrier: "Source row 204",
    origin: "Terms & conditions text",
    destination: "—",
    terms: "Not a freight rate",
    termsNote: "Paragraph of contract wording",
    equipment: "—",
    transit: "—",
    currency: "USD",
    amount: null,
    unit: "—",
    validity: "Not stated",
    transshipment: "",
    includesOriginThc: null,
    includesDestinationThc: null,
    issues: ["No rate found in row", "Destination empty"],
    issueCodes: [],
  },
];

export const PARSE_STEPS: { key: string; label: string }[] = [
  { key: "read", label: "Reading rates" },
  { key: "route", label: "Identifying routes" },
  { key: "equip", label: "Detecting equipment" },
  { key: "valid", label: "Checking validity" },
  { key: "charges", label: "Analyzing charges" },
];

export type GuidedField = "validity" | "carrier" | "ports" | "amount" | "transit" | "equipment";

export type ClarifyQuestion = {
  id?: number;
  key: RateIssueCode;
  ask: string;
  options: string[];
  done: string;
  rateIds?: number[];
  dateFollowUp?: { label: string; done: string; field?: GuidedField };
  guidedFollowUp?: { field: GuidedField; label: string; done: string };
};

export const CLARIFY_QUESTIONS: ClarifyQuestion[] = [
  {
    key: "validity",
    ask: "I found <b>3 rates</b> without a validity window. Set From/Until for these rates (or edit leftovers in the library).",
    options: ["Set validity dates", "I'll manually update them later"],
    done: "Validity %v applied. Status updated to Ready.",
    dateFollowUp: {
      label: "From / Until for these rates",
      done: "Validity %v applied. Status updated to Ready.",
    },
  },
  {
    key: "charges",
    ask: "Some rates from this file show a single all-in amount without a charge breakdown.<br>Are destination charges (THC, D/O) included or excluded?",
    options: ["Included", "Excluded", "I'll manually update them later"],
    done: "Charge basis applied. Status updated to Ready.",
  },
  {
    key: "equip",
    ask: "For some rates the equipment line reads only “40ft”. Is <b>USD 1,850</b> the rate for 40ST or 40HQ?",
    options: ["40ST", "40HQ", "I'll manually update them later"],
    done: "Equipment set. Status updated to Ready.",
  },
  {
    key: "routing",
    ask: "Some lanes mention a third port in the routing column. Is that the transshipment port?",
    options: ["Yes, transshipment", "No, direct service", "I'll manually update them later"],
    done: "Routing confirmed. Status updated to Ready.",
  },
  {
    key: "dup",
    ask: "Two different prices were found for the same lane and equipment — <b>Ningbo – Hamburg, 40HC</b>: USD 1,960 and USD 2,040. Which one should be active?",
    options: ["Keep USD 1,960", "Keep USD 2,040", "Keep both as options"],
    done: "Conflict resolved.",
  },
];

export const formatMoney = (currency: string, amount: number): string => {
  const decimals = amount < 100 ? 2 : 0;
  const body = amount.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  const code = currency.trim();

  return code ? `${code} ${body}` : body;
};

export const sellingAmount = (amount: number, markup = 0.1): number => amount * (1 + markup);

export const carrierInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return (parts[0][0] + parts[1][0]).toUpperCase();
};

export const searchBlob = (rate: RateRecord): string =>
  [rate.origin, rate.destination, rate.carrier, rate.id, rate.equipment].join(" ").toLowerCase();

export const countByStatus = (rates: RateRecord[]): Record<RateBucket, number> => ({
  all: rates.length,
  ready: rates.filter((rate) => rate.status === "ready").length,
  review: rates.filter((rate) => rate.status === "review").length,
  rejected: rates.filter((rate) => rate.status === "rejected").length,
});

export const countByMode = (rates: RateRecord[]): { fcl: number; lcl: number; air: number; land: number } => {
  const active = rates.filter((rate) => rate.status === "ready");

  return {
    fcl: active.filter((rate) => rate.mode === "ocean" && rate.service === "fcl").length,
    lcl: active.filter((rate) => rate.mode === "ocean" && rate.service === "lcl").length,
    air: active.filter((rate) => rate.mode === "air").length,
    land: active.filter((rate) => rate.mode === "road").length,
  };
};

export const filterRates = (
  rates: RateRecord[],
  query: {
    status?: RateBucket;
    mode?: "all" | RateMode;
    text?: string;
    equipment?: string;
  }
): RateRecord[] => {
  const text = (query.text ?? "").trim().toLowerCase();
  const equipment = query.equipment ?? "";

  return rates.filter((rate) => {
    if (query.status !== undefined && query.status !== "all" && rate.status !== query.status) return false;
    if (query.mode !== undefined && query.mode !== "all" && rate.mode !== query.mode) return false;
    if (text !== "" && !searchBlob(rate).includes(text)) return false;
    if (equipment !== "" && !rate.equipment.toLowerCase().includes(equipment.toLowerCase())) return false;

    return true;
  });
};

export const applyIssueGroup = (
  rates: RateRecord[],
  code: RateIssueCode,
  patch: Partial<Pick<RateRecord, "validity" | "equipment" | "termsNote">>
): RateRecord[] =>
  rates.map((rate) => {
    if (!rate.issueCodes.includes(code) || rate.status !== "review") return rate;

    const nextCodes = rate.issueCodes.filter((item) => item !== code);
    const nextIssues = rate.issues.filter((issue) => {
      if (code === "validity") return !/validity/i.test(issue);
      if (code === "equip") return !/container|equipment/i.test(issue);
      if (code === "routing") return !/origin|destination|routing|ambiguous/i.test(issue);
      if (code === "charges") return !/charge|carrier not stated|terms/i.test(issue);

      return true;
    });

    return {
      ...rate,
      ...patch,
      issueCodes: nextCodes,
      issues: nextIssues,
      status: nextCodes.length === 0 ? "ready" : rate.status,
    };
  });

/** Parse card transit display ("21 days", "21") into days; empty/dash → null. */
export const parseTransitDays = (value: string): number | null => {
  const text = value.trim();
  if (!text || text === "—" || /^n\/?a$/i.test(text) || /^not stated$/i.test(text)) return null;

  const match = text.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;

  const n = Number(match[1]);
  if (!Number.isFinite(n)) return null;

  const days = Math.round(n);
  return days >= 1 ? days : null;
};

/** Display transit as `N days` / `—` (matches rate card transit string). */
export const formatTransitLabel = (days: number | null | undefined): string => {
  if (days == null || !Number.isFinite(days) || days < 1) return "—";
  const n = Math.round(days);
  return n === 1 ? "1 day" : `${n} days`;
};

/** Pretty label for a from/until ISO pair (en-GB). */
export const formatValidityLabel = (from: string, until: string): string => {
  const pretty = (iso: string) =>
    new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  if (!from && !until) return "Not stated";
  if (from && until && from !== until) return `${pretty(from)} – ${pretty(until)}`;

  return pretty(until || from);
};

/**
 * Best-effort parse of a card validity display string into ISO dates.
 * Accepts "Not stated", single dates, and "A – B" / "A - B" / "A to B".
 */
export const parseValidityRange = (value: string): { from: string; until: string } => {
  const text = value.trim();

  if (!text || /^not stated$/i.test(text)) {
    return { from: "", until: "" };
  }

  const toIso = (chunk: string): string => {
    const trimmed = chunk.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const months: Record<string, string> = {
      jan: "01",
      january: "01",
      feb: "02",
      february: "02",
      mar: "03",
      march: "03",
      apr: "04",
      april: "04",
      may: "05",
      jun: "06",
      june: "06",
      jul: "07",
      july: "07",
      aug: "08",
      august: "08",
      sep: "09",
      sept: "09",
      september: "09",
      oct: "10",
      october: "10",
      nov: "11",
      november: "11",
      dec: "12",
      december: "12",
    };

    const match = trimmed.match(
      /^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\.?\s+(\d{4})$|^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/
    );

    if (match) {
      const day = match[1] ?? match[5];
      const monthName = (match[2] ?? match[4] ?? "").toLowerCase();
      const year = match[3] ?? match[6];
      const month = months[monthName];

      if (month && day && year) {
        return `${year}-${month}-${String(day).padStart(2, "0")}`;
      }
    }

    const parsed = Date.parse(trimmed);

    if (Number.isNaN(parsed)) return "";

    const date = new Date(parsed);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");

    return `${y}-${m}-${d}`;
  };

  const parts = text.split(/\s+[–—-]\s+|\s+to\s+/i);

  if (parts.length >= 2) {
    return { from: toIso(parts[0]), until: toIso(parts[parts.length - 1]) };
  }

  const iso = toIso(text);

  return { from: iso, until: iso };
};

const validityFilled = (value: string): boolean => {
  const text = value.trim();

  return text !== "" && !/^not stated$/i.test(text);
};

/** Charge-related labels that should keep the `charges` issue code. */
const CHARGE_LABEL_RE = /currency missing|amount missing|thc|all-?in|destination charges unclear/i;
const EQUIP_LABEL_RE = /equipment missing|equipment ambiguous|equipment|container type/i;
const ROUTING_LABEL_RE = /origin|destination|routing|ambiguous|port/i;
const THC_LABEL_RE = /destination charges unclear|thc|all-?in/i;

/** Manual card edit: filling validity / currency / amount / equip / ports / liner term clears matching flags. */
export const applyRateEdit = (current: RateRecord, draft: RateRecord): RateRecord => {
  const filled = validityFilled(draft.validity);
  const currencyFilled = draft.currency.trim() !== "";
  const amountFilled = draft.amount != null && draft.amount > 0;
  const equipmentConcrete = isConcreteEquipment(draft.equipment);
  const portsFilled = portFilled(draft.origin) && portFilled(draft.destination);
  const linerChoice = linerTermFromRate(draft);
  const thc = thcFieldsFromLinerTerm(linerChoice);
  // Unspecified removed — null flags default to FIFO and clear THC-only charge labels.
  const linerSet = true;
  const liner = linerTermLabels(linerChoice);
  const clearedCurrencyMissing = currencyFilled && draft.issues.some((issue) => /currency missing/i.test(issue));
  const clearedAmountMissing = amountFilled && draft.issues.some((issue) => /amount missing/i.test(issue));

  let issues = filled ? draft.issues.filter((issue) => !/validity/i.test(issue)) : [...draft.issues];
  if (currencyFilled) issues = issues.filter((issue) => !/currency missing/i.test(issue));
  if (amountFilled) issues = issues.filter((issue) => !/amount missing/i.test(issue));
  if (equipmentConcrete) issues = issues.filter((issue) => !EQUIP_LABEL_RE.test(issue));
  if (portsFilled) {
    issues = issues.filter((issue) => {
      if (EQUIP_LABEL_RE.test(issue) || THC_LABEL_RE.test(issue) || /charges/i.test(issue)) return true;

      return !ROUTING_LABEL_RE.test(issue);
    });
  }
  if (linerSet) issues = issues.filter((issue) => !THC_LABEL_RE.test(issue));

  let issueCodes = filled ? draft.issueCodes.filter((code) => code !== "validity") : [...draft.issueCodes];
  if (equipmentConcrete) issueCodes = issueCodes.filter((code) => code !== "equip");
  if (portsFilled) issueCodes = issueCodes.filter((code) => code !== "routing");

  const chargeLabelsRemain = issues.some((issue) => CHARGE_LABEL_RE.test(issue));
  if ((clearedCurrencyMissing || clearedAmountMissing || linerSet) && !chargeLabelsRemain) {
    issueCodes = issueCodes.filter((code) => code !== "charges");
  }

  const status = current.status === "rejected" ? "rejected" : issueCodes.length === 0 ? "ready" : "review";

  return {
    ...draft,
    ...thc,
    terms: liner.title,
    termsNote: liner.subtitle,
    issueCodes,
    issues,
    status,
  };
};

/** Turn a one-line pricing prompt into a rule card. Heuristic until T4. */
export const ruleFromPrompt = (prompt: string): PricingRule => {
  const trimmed = prompt.trim();
  const percent = trimmed.match(/(\d+(?:\.\d+)?)\s*%/);
  const flat = trimmed.match(/\$\s*(\d+(?:\.\d+)?)|(\d+(?:\.\d+)?)\s*(?:usd|dollars?)/i);
  const action = percent ? `+${percent[1]}%` : flat ? `+USD ${flat[1] ?? flat[2]}` : trimmed.slice(0, 80);

  const fromMatch = trimmed.match(/from\s+(.+?)\s+to\s+(.+?)(?:\.|$)/i);

  return {
    id: `rule-${Date.now()}`,
    title: trimmed.slice(0, 72) || "FCL markup",
    origin: fromMatch?.[1]?.trim() ?? "All origins",
    destination: fromMatch?.[2]?.trim().replace(/\.$/, "") ?? "All destinations",
    equipment: /40\s*h[qc]/i.test(trimmed) ? "40HQ" : /40/.test(trimmed) ? "40ST" : "All containers",
    action,
  };
};
